import { Aircraft, Company, CompanyBase, Job, Person, RiskAssessmentFactor, Ticket, TicketLineItem, TicketLocation } from "../sharedCode/avroGraphql";
import { calculateLookupKeyHash, formatNumericInput } from "./UtilityMethods";

import { GetLineItemPrecision } from "../sharedCode/HelperMethods";

export type LocalSetting = {
    key: string;
    value: string;
};

export type LogEntry = {
    id: number;
    text: string;
    timestamp: string;
};

export type PartialSyncSnapshot = {
    timestamp: string;
    originalTicket: Record<string, unknown>;
    serverResponse: Record<string, unknown>;
};

// this is a straight copy of the generated Maybe class, just so the app code is not tied to the generated code
export type MaybeExtended<T> = T | null;

type extendedAttributes = {
    selectName: string;
};

export type AircraftExtended = Aircraft & extendedAttributes;

export function ExtendAircraft(aircraft: Aircraft): AircraftExtended {
    const selectName = `${aircraft.callsign} (${aircraft.AircraftType.name})`.toLocaleUpperCase();
    return { ...aircraft, selectName };
}

export type CompanyExtended = Company &
    extendedAttributes & {
        fullAddress: string;
    };

export function ExtendCompany(company: Company, companyList: Company[]): CompanyExtended {
    // const selectName = `${company.name}${
    //     company.ho_company_id ? ` (${companyList.find((c) => c.id === company.ho_company_id)?.name})` : ""
    // }`.toLocaleUpperCase();
    const selectName = company.name.toLocaleUpperCase();
    const fullAddress = `${company.address1}${optionalValue(" ", company.address2)}${optionalValue(", ", company.city)}${optionalValue(
        ", ",
        company.prov_state_code2
    )}`.toLocaleUpperCase();
    return { ...company, selectName, fullAddress };
}

function optionalValue(separator: string, value: MaybeExtended<string> | undefined): string {
    return value === undefined || value === null || value === "" ? "" : `${separator}${value}`;
}

export type CompanyBaseExtended = CompanyBase & extendedAttributes;

export function ExtendCompanyBase(base: CompanyBase): CompanyBaseExtended {
    const selectName = `${base.label} (${base.airport_code})`.toLocaleUpperCase();
    return { ...base, selectName };
}

export type JobExtended = Job & extendedAttributes;

export function ExtendJob(job: Job, jobList: Job[]): JobExtended {
    const selectName = `${job.label ?? "unlabelled"}${job.parent_job_id ? ` (Parent ${jobList.find((j) => j.id === job.parent_job_id)?.label ?? "unlabelled"})` : ""} (PO#: ${
        job.customer_po ?? "n/a"
    })  (Start: ${job.start_date}) (${job.JobType.label}) (${job.JobStatus.label})`;
    return { ...job, selectName: selectName.toLocaleUpperCase() };
}

export type RiskAssessmentFactorExtended = RiskAssessmentFactor & extendedAttributes;

export function buildRiskAssessmentFactorSelectName(riskAssessmentFactor: RiskAssessmentFactor, riskAssessmentFactorList: RiskAssessmentFactor[]): string {
    // return `${riskAssessmentFactor.label}${riskAssessmentFactor.id ? ` (${riskAssessmentFactorList.find((r) => r.id === riskAssessmentFactor.id)?.label})` : ""}`;
    return `${riskAssessmentFactor.label} (${riskAssessmentFactor.id})`;
}

export type PersonExtended = Person & extendedAttributes;

export function ExtendPerson(person: Person): PersonExtended {
    const selectName = `${person.first_name} ${person.last_name}`.toLocaleUpperCase();
    return { ...person, selectName };
}

// remember to update TicketMerger if you add any fields
export type LocalTicketFields = {
    hasLocalChanges: boolean;
    nextLocalId: number; // NB - use the current value and then decrement/save
    OpenedCards: string[];
    pushAttempts: number;
    isDemoTicket: boolean;
    isDuplicateTicket: boolean;
    partialSyncSnapshot?: PartialSyncSnapshot;
};
export type TicketExtended = Ticket & LocalTicketFields;

export function ExtendTicket(ticket: Ticket): TicketExtended {
    return {
        ...ticket,
        hasLocalChanges: false,
        nextLocalId: -1,
        OpenedCards: [],
        pushAttempts: 0,
        isDemoTicket: false,
        isDuplicateTicket: false,
        partialSyncSnapshot: undefined,
    };
}

export type TicketLocationExtended = TicketLocation & {
    inputHours: string;
    keyHash: string | undefined;
};

export function ExtendTicketLocation(ticket: Ticket, ticketLocation: TicketLocation): TicketLocationExtended {
    const keyHash = calculateLookupKeyHash(ticket.company_id, ticketLocation);
    return { ...ticketLocation, inputHours: formatNumericInput(ticketLocation.hours, 1), keyHash };
}

export type TicketLineItemExtended = TicketLineItem & {
    inputValue: string;
};

export function ExtendTicketLineItem(lineItem: TicketLineItem): TicketLineItemExtended {
    const precision = GetLineItemPrecision(lineItem.line_item_type_id);
    const valueField = precision === 0 ? "quantity" : "amount";
    return { ...lineItem, inputValue: formatNumericInput(lineItem[valueField], precision) };
}

export type CostCodeLookup = {
    keyHash: string;
    company_id: number;
    location: string;
    cost_code: string;
};
